import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_execution-hub-52/artifacts/f0e2q5kr_40434786-BC05-497A-BBE0-931848E37F84-removebg-preview.png";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/sectors", label: "Sectors" },
    { to: "/sectors/defence-aerospace", label: "Defence" },
    { to: "/sectors/commodities-international-trade", label: "Commodities" },
    { to: "/healthcare", label: "Healthcare" },
    { to: "/contact", label: "Contact" },
];

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header
            data-testid="main-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-[#040B1A]/90 backdrop-blur-xl border-b border-[#C9A14A]/20"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="nav-logo-link">
                    <img src={LOGO} alt="Indravax" className="h-12 w-12 object-contain" />
                    <div className="hidden sm:block leading-tight whitespace-nowrap">
                        <div className="font-serif text-xl text-white tracking-wide">INDRAVAX</div>
                        <div className="text-[0.58rem] tracking-[0.22em] text-[#C9A14A] uppercase">Strategic Advisory LLP</div>
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-6">
                    {LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.to === "/"}
                            data-testid={`nav-link-${link.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `relative text-[0.68rem] tracking-[0.22em] uppercase font-medium transition-colors duration-300 ${
                                    isActive ? "text-[#C9A14A]" : "text-slate-300 hover:text-[#C9A14A]"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {link.label}
                                    <span
                                        className={`absolute -bottom-2 left-0 h-px bg-[#C9A14A] transition-all duration-500 ${
                                            isActive ? "w-full" : "w-0"
                                        }`}
                                    />
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <Link
                    to="/contact"
                    data-testid="nav-cta-consultation"
                    className="hidden xl:inline-flex btn-secondary text-[0.65rem] py-3 px-5 whitespace-nowrap"
                >
                    Request Consultation
                </Link>

                <button
                    onClick={() => setOpen(!open)}
                    className="xl:hidden text-[#C9A14A]"
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile menu */}
            <div
                className={`xl:hidden overflow-hidden transition-all duration-500 ease-out bg-[#040B1A]/95 backdrop-blur-xl border-t border-[#C9A14A]/10 ${
                    open ? "max-h-[600px]" : "max-h-0"
                }`}
            >
                <nav className="max-w-7xl mx-auto px-6 py-8 flex flex-col gap-5">
                    {LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.to === "/"}
                            data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm tracking-[0.22em] uppercase ${isActive ? "text-[#C9A14A]" : "text-slate-300"}`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                    <Link to="/contact" className="btn-primary text-xs mt-4" data-testid="nav-mobile-cta">
                        Request Consultation
                    </Link>
                </nav>
            </div>
        </header>
    );
};

export default Navbar;
